import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Clock, Users, Play, Search, Filter } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useGlobalSearch from '../../hooks/useGlobalSearch';

const CourseCatalog = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400, () => setCurrentPage(1));
  const [statusFilter, setStatusFilter] = useState('active');
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  useEffect(() => {
    fetchCourses();
    fetchEnrolledCourses();
  }, [statusFilter, debouncedSearch]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const params = { status: statusFilter };
      if (debouncedSearch) params.search = debouncedSearch;

      const { data } = await api.get('/courses', { params });
      if (data.success) {
        setCourses(data.data);
        setCurrentPage(1);
      }
    } catch (error) {
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      const { data } = await api.get('/enrollments/my-courses');
      if (data.success) {
        setEnrolledCourses(data.data.map(e => e.course._id));
      }
    } catch (error) {
      console.error('Failed to fetch enrolled courses:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      const { data } = await api.post('/enrollments', { courseId });
      if (data.success) {
        toast.success('Successfully enrolled in course!');
        setEnrolledCourses([...enrolledCourses, courseId]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to enroll in course');
    }
  };

  const handleSearch = () => {
    fetchCourses();
  };

  const isEnrolled = (courseId) => enrolledCourses.includes(courseId);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = courses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(courses.length / itemsPerPage);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff]">Course Catalog</h1>
        <p className="text-sm text-gray-500 mt-1">Browse and enroll in available courses</p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-[#1a3884] hover:bg-[#00152E] text-white rounded-lg font-medium transition-colors"
          >
            Search
          </button>
        </div>
      </div>

      {/* Course Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a3884]"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No courses available</p>
          </div>
        ) : (
          currentItems.map((course) => (
            <div
              key={course._id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Course Banner */}
              <div className="h-48 bg-gradient-to-br from-[#00152E] to-[#1a3884] relative">
                {course.banner ? (
                  <img
                    src={course.banner}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <BookOpen className="w-16 h-16 text-white opacity-50" />
                  </div>
                )}
                {isEnrolled(course._id) && (
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Enrolled
                  </div>
                )}
              </div>

              {/* Course Content */}
              <div className="p-6">
                <div className="mb-2">
                  <span className="text-xs font-medium text-[#1a3884] bg-[#00152E]/5 px-2 py-1 rounded">
                    {course.courseCode}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {course.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {course.description || 'No description available'}
                </p>

                {/* Course Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    <span>{course.modules?.length || 0} modules</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{course.duration || 0} hrs</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{course.enrollmentCount || 0}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {isEnrolled(course._id) ? (
                    <button
                      onClick={() => navigate(`/student/courses/${course._id}/player`)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] text-white rounded-lg font-medium transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Continue Learning
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => navigate(`/courses/${course._id}/preview`)}
                        className="flex-1 px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => handleEnroll(course._id)}
                        className="flex-1 px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] text-white rounded-lg font-medium transition-colors"
                      >
                        Enroll Now
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-100 pt-6">
          <div className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, courses.length)} of {courses.length} courses
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                currentPage === 1
                  ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
              }`}
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                    currentPage === i + 1
                      ? 'bg-[#1a3884] text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                currentPage === totalPages
                  ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCatalog;
